// src/components/videocall/components/VideoStreams.tsx
import { useEffect, useRef, useState } from 'react';
import { Users, Video as VideoIcon, Play } from 'lucide-react';
import type { Annotation, ConnectionStatus, Measurement } from '../types';
import type { Socket } from 'socket.io-client';
import { useDeviceOrientation } from '../hooks/useDeviceOrientation';

// CSS to aggressively hide all native video controls
const videoControlStyles = `
  video::-webkit-media-controls {
    display: none !important;
  }
  video::-webkit-media-controls-enclosure {
    display: none !important;
  }
  video::-webkit-media-controls-panel {
    display: none !important;
  }
  video::-webkit-media-controls-play-button {
    display: none !important;
  }
  video::-webkit-media-controls-start-playback-button {
    display: none !important;
  }
  video::-webkit-media-controls-overlay-play-button {
    display: none !important;
  }
  video::--webkit-media-controls-timeline {
    display: none !important;
  }
  video::-webkit-media-controls-current-time-display {
    display: none !important;
  }
  video::-webkit-media-controls-time-remaining-display {
    display: none !important;
  }
  video::-webkit-media-controls-mute-button {
    display: none !important;
  }
  video::-webkit-media-controls-volume-slider {
    display: none !important;
  }
  video::-webkit-media-controls-fullscreen-button {
    display: none !important;
  }

  /* Smooth orientation transitions - FaceTime-style */
  .orientation-transition-container {
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1) !important;
    will-change: transform;
  }
  .orientation-transition-video {
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                opacity 0.2s ease !important;
    will-change: transform;
  }
`;

interface VideoStreamsProps {
  localVideoRef: React.RefObject<HTMLVideoElement>;
  remoteVideoRef: React.RefObject<HTMLVideoElement>;
  remoteConnected: boolean;
  connectionStatus: ConnectionStatus;
  roomId: string;
  participantName: string;
  guestName?: string; // Name of guest to show in "Waiting for..." message
  annotations: Annotation[];
  isAnnotating: boolean;
  isHost: boolean;
  isChatOpen: boolean; // still accepted, even if we don't rely on it now
  isMobileView: boolean; // to determine object-fit behavior
  selectedAnnotationTool: 'pencil' | 'square' | 'circle' | 'arrow' | null;
  handleAnnotationMouseDown: (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => void;
  handleAnnotationMouseMove: (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => void;
  handleAnnotationMouseUp: (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => void;
  isVideoOff: boolean;
  remoteVideoEnabled: boolean;
  remoteVideoMuted?: boolean; // true when AudioContext handles audio (iOS)
  // Measurement props
  measurements: Measurement[];
  // Camera props
  facingMode: 'user' | 'environment';
  // Socket for orientation sync
  socket: React.MutableRefObject<Socket | null>;
  /** Ref for recording - host updates this so recording uses same orientation as live display */
  remoteOrientationRef?: React.MutableRefObject<number>;
}

export function VideoStreams({
  localVideoRef,
  remoteVideoRef,
  remoteConnected,
  connectionStatus,
  roomId,
  participantName,
  guestName,
  annotations,
  isAnnotating,
  isHost,
  isChatOpen, // kept for compatibility
  isMobileView,
  selectedAnnotationTool,
  handleAnnotationMouseDown,
  handleAnnotationMouseMove,
  handleAnnotationMouseUp,
  isVideoOff,
  remoteVideoEnabled,
  showTapPrompt,
  handleTapToPlay,
  remoteVideoMuted = false,
  measurements,
  facingMode,
  socket,
  remoteOrientationRef,
}: VideoStreamsProps) {
  // 🔥 CORRECT LOGIC:
  // - Host ALWAYS sees: Guest in MAIN (when connected), Waiting overlay (when not connected)
  // - Guest ALWAYS sees: Themselves in MAIN
  // - PIP ALWAYS shows: Host
  // - Participant name on top-left shows who is in MAIN view
  // - NEVER show host name in main window (since main window is always client/guest)

  const showWaitingOverlay = isHost && !remoteConnected;
  const mainLabel = isHost ? (remoteConnected ? (participantName || 'Guest') : 'Waiting...') : 'You';

  // ✅ Get device orientation for local video rotation (guest only)
  const { rotationAngle: localRotationAngle, orientation: localOrientation, isLandscape: isDeviceLandscape, isTransitioning: isLocalTransitioning } = useDeviceOrientation();

  // 🎨 DEBUG: Log guest orientation changes
  useEffect(() => {
    if (!isHost) {
      console.log('[VideoStreams] 📱 Guest orientation changed:', {
        orientation: localOrientation,
        rotationAngle: localRotationAngle,
        facingMode
      });
    }
  }, [isHost, localOrientation, localRotationAngle, facingMode]);

  // ✅ Get host device orientation for rotating guest feed container (host only)
  const { rotationAngle: hostRotationAngle } = useDeviceOrientation();

  // ✅ Detect remote participant orientation from device angle
  const [remoteVideoRotation, setRemoteVideoRotation] = useState(0);
  const [remoteFacingMode, setRemoteFacingMode] = useState<'user' | 'environment'>('user');
  const [guestDeviceAngle, setGuestDeviceAngle] = useState<number>(0);
  const [hostDeviceAngle, setHostDeviceAngle] = useState<number>(0);
  const [pipRotation, setPipRotation] = useState<number>(0);

  // Listen for remote participant orientation via Socket.IO
  useEffect(() => {
    if (!socket.current) return;

    const handleOrientationUpdate = (data: any) => {
      if (isHost) {
        // Host receives guest orientation for main video
        console.log('[VideoOrientation] 📱 Host received guest device angle:', data.rotationAngle);
        setGuestDeviceAngle(data.rotationAngle);
        // Sync to recording ref - same value as live display (single source of truth)
        if (remoteOrientationRef) {
          const r = data.rotationAngle ?? 0;
          remoteOrientationRef.current = r === -90 ? 90 : r === 90 ? 270 : r;
        }
        if (data.facingMode) {
          console.log('[VideoOrientation] 📷 Guest camera switched to:', data.facingMode, '(prev:', remoteFacingMode, ')');
          setRemoteFacingMode(data.facingMode);
        }
      } else {
        // Guest receives host orientation for PIP video
        console.log('[VideoOrientation] 📱 Guest received host device angle:', data.rotationAngle);
        setHostDeviceAngle(data.rotationAngle);
        if (data.facingMode) {
          console.log('[VideoOrientation] 📷 Host camera switched to:', data.facingMode);
          setRemoteFacingMode(data.facingMode);
        }
      }
    };

    socket.current.on('device-orientation', handleOrientationUpdate);
    return () => {
      socket.current?.off('device-orientation', handleOrientationUpdate);
    };
  }, [isHost, socket, remoteFacingMode]);

  // Calculate rotation based ONLY on guest device angle (not video dimensions - iOS doesn't rotate the stream)
  useEffect(() => {
    if (!isHost || !remoteConnected) {
      return;
    }

    console.log('[VideoOrientation] 📐 Guest device angle changed:', guestDeviceAngle);

    // iOS streams are ALWAYS 480x640 (portrait), so we must rotate based on device angle alone
    // rotationAngle from guest: 0=portrait, -90=landscape-primary, 90=landscape-secondary
    // Apply guestDeviceAngle directly: -90 corrects sideways video when guest is in landscape
    const rotation = guestDeviceAngle;

    // Debug: Check if we should use 45% wrapper or full-screen
    const shouldUse45PercentWrapper = remoteFacingMode === 'user' && guestDeviceAngle === 0;
    console.log('[VideoOrientation] 📱 Layout decision:', {
      guestDeviceAngle,
      facingMode: remoteFacingMode,
      isPortrait: guestDeviceAngle === 0,
      shouldUse45PercentWrapper,
      layout: shouldUse45PercentWrapper ? '45% WRAPPER (portrait front camera)' : 'FULL-SCREEN (landscape or back camera)'
    });

    console.log('[VideoOrientation] 🔄 Calculated rotation:', {
      guestDeviceAngle,
      facingMode: remoteFacingMode,
      appliedRotation: rotation,
      mapping: `${remoteFacingMode} camera: ${guestDeviceAngle}° → ${rotation}°`
    });

    setRemoteVideoRotation(rotation);
  }, [isHost, remoteConnected, guestDeviceAngle, remoteFacingMode]);

  // ✅ Calculate PIP rotation for guest viewing host
  useEffect(() => {
    if (isHost || !remoteConnected) {
      // Host doesn't need PIP rotation (sees own video)
      setPipRotation(0);
      return;
    }

    // Guest viewing host in PIP - apply rotation correction
    console.log('[VideoOrientation] 📐 Host device angle changed:', hostDeviceAngle);

    // ✅ IMPORTANT: Desktop vs Mobile camera behavior
    // Desktop webcam: Always landscape (640x480), angle = 0°, no rotation needed
    // Mobile front camera: Portrait stream (480x640) rotated -90°, needs +90° correction
    // Mobile back camera: Landscape, minimal correction needed

    let rotation = 0;

    // ✅ FIX: Simply negate the device angle for all cases
    // Desktop: angle = 0° → rotation = 0° (no change)
    // Mobile: angle = -90° → rotation = +90° (corrects orientation)
    rotation = -hostDeviceAngle;

    console.log('[VideoOrientation] 🔄 Calculated PIP rotation:', {
      hostDeviceAngle,
      facingMode: remoteFacingMode,
      calculatedRotation: rotation,
      deviceType: hostDeviceAngle === 0 ? 'Desktop (no rotation)' : 'Mobile (needs rotation)',
      explanation: hostDeviceAngle === 0
        ? 'Desktop webcam - already upright'
        : `Mobile rotated ${hostDeviceAngle}° → correcting with ${rotation}°`
    });

    setPipRotation(rotation);
  }, [isHost, remoteConnected, hostDeviceAngle, remoteFacingMode]);

  // Inject CSS to hide video controls
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'video-control-hiding-styles';
    style.textContent = videoControlStyles;
    document.head.appendChild(style);

    console.log('✅ Injected video control hiding styles');

    return () => {
      const existingStyle = document.getElementById('video-control-hiding-styles');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  // Debug: log annotations
  useEffect(() => {
    if (annotations.length > 0) {
      console.log('🎨 Rendering annotations:', annotations.length, annotations);
    }
  }, [annotations]);

  // Debug: log rotation changes
  useEffect(() => {
    if (isHost) {
      const combinedRotation = remoteVideoRotation - hostRotationAngle;
      console.log('[VideoOrientation] 🔄 Rotation state changed:', {
        guestRotation: remoteVideoRotation,
        hostRotation: hostRotationAngle,
        combinedRotation: combinedRotation,
        guestFacingMode: remoteFacingMode,
        guestMirrored: remoteFacingMode === 'user',
        transform: combinedRotation !== 0 ? `rotate(${combinedRotation}deg)` : 'none'
      });
    }
  }, [remoteVideoRotation, hostRotationAngle, remoteFacingMode, isHost]);

  // PIP video ref - prevents refresh on every render
  const pipVideoRef = useRef<HTMLVideoElement>(null);

  // Update PIP srcObject - poll to ensure it's set when available
  useEffect(() => {
    const updatePipVideo = () => {
      if (!pipVideoRef.current) return;

      const sourceRef = isHost ? localVideoRef : remoteVideoRef;
      const srcObject = sourceRef.current?.srcObject;

      if (srcObject && pipVideoRef.current.srcObject !== srcObject) {
        console.log('🎥 Setting PIP video srcObject for', isHost ? 'host' : 'guest');
        pipVideoRef.current.srcObject = srcObject;
        pipVideoRef.current.play().catch(e => console.warn('PIP play failed:', e));
      }
    };

    // Initial update
    updatePipVideo();

    // Poll every 500ms to catch when srcObject becomes available
    const interval = setInterval(updatePipVideo, 500);

    return () => clearInterval(interval);
  }, [isHost, localVideoRef, remoteVideoRef, remoteConnected]);

  return (
    <div className="relative flex-1 overflow-hidden">
      {/* Orientation transition overlay — dims screen briefly during rotation like FaceTime */}
      <div
        className="absolute inset-0 bg-black pointer-events-none"
        style={{
          zIndex: 99,
          opacity: isLocalTransitioning ? 0.6 : 0,
          transition: isLocalTransitioning
            ? 'opacity 0.1s ease-in'    // Fade in quickly when rotation starts
            : 'opacity 0.4s ease-out',  // Fade out slowly after settling
        }}
      />
      {/* MAIN VIDEO CONTAINER */}
      <div className="absolute inset-0 flex items-center justify-center bg-black">
        {/* VIDEO CONTAINER - No rotation, let browser handle orientation naturally */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            // No border on container - borders are on video elements themselves
          }}
        >
          {/* LOCAL VIDEO in MAIN - Always rendered */}
          {/* Shown for: Guest (always), Host (NEVER - host always in PIP only) */}
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted={true}
            controls={false}
            disablePictureInPicture
            disableRemotePlayback
            controlsList="nodownload nofullscreen noremoteplayback"
            preload="auto"
            className="absolute inset-0 w-full h-full bg-black orientation-transition-video"
            style={{
              // Use 'contain' for consistent framing (no zoom/crop); matches remote video strategy
              objectFit: 'contain',
              // Mirror front camera for natural selfie view, don't mirror back camera
              transform: !isHost && facingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)',
              // Host video should NEVER show in main area - only in PIP
              // Guest always sees own video in main area
              opacity: !isHost ? 1 : 0,
              visibility: !isHost ? 'visible' : 'hidden',
              zIndex: !isHost ? 2 : 0,
              pointerEvents: 'none',
            }}
            // @ts-ignore - webkit specific attributes
            webkit-playsinline="true"
            x-webkit-airplay="deny"
            x5-playsinline="true"
            x5-video-player-type="h5"
            x5-video-player-fullscreen="false"
            x5-video-orientation="portrait"
          />

          {/* REMOTE VIDEO in MAIN - Always rendered as single element */}
          {/* Shown for: Host (when guest connected) */}
          {/* Desktop: Wrapped in rounded container with margins, natural fit (no zoom) */}
          {/* Mobile: Full screen */}
          {!isMobileView ? (
            // ✅ FIX: Desktop - Full screen like FaceTime (no excessive margins)
            <div
              className="absolute inset-0"
              style={{
                overflow: 'hidden',
                backgroundColor: '#000',
                opacity: (isHost && remoteConnected) ? 1 : 0,
                visibility: (isHost && remoteConnected) ? 'visible' : 'hidden',
                zIndex: (isHost && remoteConnected) ? 2 : -1,
              }}
            >
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                muted={remoteVideoMuted}
                controls={false}
                disablePictureInPicture
                disableRemotePlayback
                controlsList="nodownload nofullscreen noremoteplayback"
                preload="auto"
                id="video-feed-wrapper"
                className="w-full h-full orientation-transition-video"
                style={{
                  // Use 'contain' so host sees full guest video (no zoom/crop)
                  objectFit: 'contain',
                  // Apply rotation for guest device orientation + mirror front camera
                  // Only subtract hostRotationAngle when host is on mobile (isMobileView); desktop stays 0
                  transform: (() => {
                    const hostRot = isMobileView ? hostRotationAngle : 0;
                    const rot = isHost && remoteConnected ? remoteVideoRotation - hostRot : 0;
                    const mirror = remoteFacingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)';
                    return rot !== 0 ? `rotate(${rot}deg) ${mirror}` : mirror;
                  })(),
                  pointerEvents: 'none',
                }}
                onLoadedMetadata={() => {
                  console.log('[VideoStreams] 🎥 Desktop remote video loaded, remoteFacingMode:', remoteFacingMode);
                }}
                // @ts-ignore - webkit specific attributes
                webkit-playsinline="true"
                x-webkit-airplay="deny"
                x5-playsinline="true"
                x5-video-player-type="h5"
                x5-video-player-fullscreen="false"
                x5-video-orientation={guestDeviceAngle !== 0 ? "landscape" : "portrait"}
              />
            </div>
          ) : (
            // Mobile: Full screen like FaceTime
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              muted={remoteVideoMuted}
              controls={false}
              disablePictureInPicture
              disableRemotePlayback
              controlsList="nodownload nofullscreen noremoteplayback"
              preload="auto"
              id="video-feed-wrapper"
              className="absolute inset-0 w-full h-full bg-black orientation-transition-video"
              style={{
                // Use 'contain' so host sees full guest video (no zoom/crop)
                objectFit: 'contain',
                // Apply rotation for guest device orientation + mirror front camera
                // Only subtract hostRotationAngle when host is on mobile; desktop stays 0
                transform: (() => {
                  const hostRot = isMobileView ? hostRotationAngle : 0;
                  const rot = isHost && remoteConnected ? remoteVideoRotation - hostRot : 0;
                  const mirror = remoteFacingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)';
                  return rot !== 0 ? `rotate(${rot}deg) ${mirror}` : mirror;
                })(),
                opacity: 1,
                visibility: 'visible',
                position: (isHost && remoteConnected) ? 'absolute' : 'absolute',
                left: (isHost && remoteConnected) ? 0 : '-9999px',
                zIndex: (isHost && remoteConnected) ? 2 : -1,
                pointerEvents: 'none',
              }}
              // @ts-ignore - webkit specific attributes
              webkit-playsinline="true"
              x-webkit-airplay="deny"
              x5-playsinline="true"
              x5-video-player-type="h5"
              x5-video-player-fullscreen="false"
              x5-video-orientation={guestDeviceAngle !== 0 ? "landscape" : "portrait"}
            />
          )}

        </div>

        {/* Video Off Overlay - MAIN window */}
        {/* For Guest: show when their own video is off */}
        {/* For Host: show when guest's video is off */}
        {((!isHost && isVideoOff) || (isHost && remoteConnected && !remoteVideoEnabled)) && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-[5]">
            <div className="text-center">
              <div className="w-24 h-24 bg-gray-800 rounded-full mx-auto mb-4 flex items-center justify-center">
                <VideoIcon className="w-12 h-12 text-gray-500" />
              </div>
              <p className="text-white text-lg font-medium">
                {!isHost ? 'Camera is off' : 'Camera is off'}
              </p>
            </div>
          </div>
        )}

        {/* Waiting overlay - ONLY show when host is waiting for guest */}
        {showWaitingOverlay && (
          <div className="absolute inset-0 flex items-center justify-center text-center z-10 px-4">
            <div className="max-w-sm w-full px-6 py-6 bg-black/60 rounded-2xl backdrop-blur-md border border-white/10">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-gray-700 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Users className="w-10 h-10 md:w-12 md:h-12 text-gray-300" />
              </div>
              <h3 className="text-base md:text-lg font-semibold text-white mb-1">
                {guestName ? `Waiting for ${guestName}...` : 'Waiting for participant...'}
              </h3>
              <p className="text-xs text-gray-300">
                Status: {connectionStatus || 'initializing'}
              </p>
            </div>
          </div>
        )}

        {/* Participant Name Badge */}
        <div className="absolute top-4 left-4 z-10">
          <div className="bg-black/70 backdrop-blur-sm text-white text-sm font-medium px-3 py-1.5 rounded-full shadow-lg">
            {mainLabel}
          </div>
        </div>

        {/* Annotation Overlay - Matches video feed wrapper exactly */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            zIndex: 20,
            pointerEvents: 'none', // Let clicks pass through outer container
          }}
        >
          {/* Container that matches video feed dimensions */}
          <div
            data-annotation-container
            className="absolute"
            style={{
              // ✅ FIX: Annotation overlay matches full-screen video on both desktop and mobile
              inset: 0,
              pointerEvents: 'none',
            }}
          >
            {/* Interactive drawing overlay */}
            {isAnnotating && selectedAnnotationTool && isHost && (
              <div
                data-annotation-overlay
                className="absolute inset-0"
                style={{
                  cursor: 'crosshair',
                  pointerEvents: 'auto',
                  touchAction: 'none',
                  zIndex: 20,
                }}
                onMouseDown={handleAnnotationMouseDown}
                onMouseMove={handleAnnotationMouseMove}
                onMouseUp={handleAnnotationMouseUp}
                onMouseLeave={handleAnnotationMouseUp}
                onTouchStart={handleAnnotationMouseDown}
                onTouchMove={handleAnnotationMouseMove}
                onTouchEnd={handleAnnotationMouseUp}
              />
            )}

          {/* Render existing annotations */}
          {annotations.map((annotation, index) => (
            <div
              key={annotation.id}
              className="absolute inset-0"
              style={{
                zIndex: 21 + index,
                pointerEvents: 'none',
              }}
            >
              {annotation.type === 'pencil' &&
                annotation.path &&
                annotation.path.length > 0 && (
                  <svg
                    className="absolute inset-0 w-full h-full"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    style={{ pointerEvents: 'none' }}
                  >
                    <path
                      d={annotation.path.reduce((path, point, idx, arr) => {
                        if (idx === 0) {
                          return `M ${point.x} ${point.y}`;
                        }
                        // Simple line segments for smooth, continuous drawing
                        return `${path} L ${point.x} ${point.y}`;
                      }, '')}
                      stroke={annotation.color}
                      strokeWidth="4"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                      style={{
                        pointerEvents: 'none',
                      }}
                    />
                  </svg>
                )}

              {annotation.type === 'circle' &&
                annotation.width &&
                annotation.height && (
                  <div
                    className="absolute rounded-full"
                    style={{
                      left: `${annotation.x}%`,
                      top: `${annotation.y}%`,
                      width: `${Math.max(annotation.width, annotation.height)}%`,
                      height: `${Math.max(annotation.width, annotation.height)}%`,
                      border: `5px solid ${annotation.color}`,
                      backgroundColor: 'transparent',
                      pointerEvents: 'none',
                    }}
                  />
                )}

              {annotation.type === 'square' &&
                annotation.width &&
                annotation.height && (
                  <div
                    className="absolute"
                    style={{
                      left: `${annotation.x}%`,
                      top: `${annotation.y}%`,
                      width: `${annotation.width}%`,
                      height: `${annotation.height}%`,
                      border: `5px solid ${annotation.color}`,
                      backgroundColor: 'transparent',
                      pointerEvents: 'none',
                    }}
                  />
                )}

              {annotation.type === 'arrow' &&
                annotation.endX !== undefined &&
                annotation.endY !== undefined && (
                  <svg
                    className="absolute inset-0 w-full h-full"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    style={{ pointerEvents: 'none' }}
                  >
                    <defs>
                      <marker
                        id={`arrowhead-${annotation.id}`}
                        markerWidth="10"
                        markerHeight="10"
                        refX="9"
                        refY="3"
                        orient="auto"
                      >
                        <polygon
                          points="0 0, 10 3, 0 6"
                          fill={annotation.color}
                        />
                      </marker>
                    </defs>
                    <line
                      x1={annotation.x}
                      y1={annotation.y}
                      x2={annotation.endX}
                      y2={annotation.endY}
                      stroke={annotation.color}
                      strokeWidth="3"
                      markerEnd={`url(#arrowhead-${annotation.id})`}
                      vectorEffect="non-scaling-stroke"
                      style={{
                        cursor: isHost ? 'grab' : 'default',
                      }}
                    />
                  </svg>
                )}
            </div>
          ))}

          {/* Render completed measurements */}
          {measurements.map((measurement, index) => (
            <div
              key={measurement.id}
              className="absolute inset-0"
              style={{
                zIndex: 42 + index,
                pointerEvents: 'none',
              }}
            >
              {/* Point-to-point measurement line */}
              {measurement.type === 'point_to_point' &&
                measurement.point1 &&
                measurement.point2 && (
                  <svg
                    className="absolute inset-0 w-full h-full"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    style={{ pointerEvents: 'none' }}
                  >
                    {/* Measurement line */}
                    <line
                      x1={measurement.point1.x}
                      y1={measurement.point1.y}
                      x2={measurement.point2.x}
                      y2={measurement.point2.y}
                      stroke="#10b981"
                      strokeWidth="3"
                      vectorEffect="non-scaling-stroke"
                    />
                    {/* Start point */}
                    <circle
                      cx={measurement.point1.x}
                      cy={measurement.point1.y}
                      r="1.5"
                      fill="#10b981"
                      vectorEffect="non-scaling-stroke"
                    />
                    {/* End point */}
                    <circle
                      cx={measurement.point2.x}
                      cy={measurement.point2.y}
                      r="1.5"
                      fill="#10b981"
                      vectorEffect="non-scaling-stroke"
                    />
                    {/* Label background */}
                    <text
                      x={(measurement.point1.x + measurement.point2.x) / 2}
                      y={(measurement.point1.y + measurement.point2.y) / 2 - 2}
                      fill="#10b981"
                      fontSize="4"
                      fontWeight="bold"
                      textAnchor="middle"
                      style={{ pointerEvents: 'none' }}
                    >
                      {measurement.real_distance_inches
                        ? `${measurement.real_distance_inches.toFixed(1)}"`
                        : '—'}
                    </text>
                  </svg>
                )}

              {/* Rectangle area measurement */}
              {measurement.type === 'rectangle_area' && measurement.rect && (
                <div
                  className="absolute"
                  style={{
                    left: `${measurement.rect.x}%`,
                    top: `${measurement.rect.y}%`,
                    width: `${measurement.rect.width}%`,
                    height: `${measurement.rect.height}%`,
                    border: '3px solid #10b981',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    pointerEvents: 'none',
                  }}
                >
                  {/* Area label */}
                  <div
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded shadow-lg whitespace-nowrap"
                    style={{ pointerEvents: 'none' }}
                  >
                    {measurement.width_inches && measurement.height_inches
                      ? `${measurement.width_inches.toFixed(1)}" × ${measurement.height_inches.toFixed(1)}"`
                      : '—'}
                    <br />
                    {measurement.area_inches2
                      ? `${measurement.area_inches2.toFixed(1)} sq in`
                      : '—'}
                  </div>
                </div>
              )}

              {/* Circle area measurement */}
              {measurement.type === 'circle_area' && measurement.circle && (
                <div
                  className="absolute rounded-full"
                  style={{
                    left: `${measurement.circle.x - measurement.circle.radius}%`,
                    top: `${measurement.circle.y - measurement.circle.radius}%`,
                    width: `${measurement.circle.radius * 2}%`,
                    height: `${measurement.circle.radius * 2}%`,
                    border: '3px solid #10b981',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    pointerEvents: 'none',
                  }}
                >
                  {/* Area label */}
                  <div
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded shadow-lg whitespace-nowrap"
                    style={{ pointerEvents: 'none' }}
                  >
                    {measurement.area_inches2
                      ? `${measurement.area_inches2.toFixed(1)} sq in`
                      : '—'}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Measurement overlay removed */}
          </div>
        </div>
      </div>

      {/* PIP VIDEO - Mobile: TOP RIGHT with safe-area, Desktop: BOTTOM RIGHT */}
      <div
        className="absolute w-32 h-24 md:w-48 md:h-36 bg-black rounded-xl overflow-hidden border-2 border-white/30 shadow-2xl"
        style={{
          // Mobile: top-right with safe-area
          top: isMobileView ? 'max(16px, env(safe-area-inset-top, 16px))' : 'auto',
          right: isMobileView ? 'max(16px, env(safe-area-inset-right, 16px))' : '16px',
          bottom: isMobileView ? 'auto' : '112px', // Desktop: above toolbar
          // VERY LOW z-index to stay below toolbar (z-85)
          zIndex: 40,
          opacity: 1,
          pointerEvents: 'none',
          // Hide when chat/notes panels are open on mobile
          display: (isMobileView && isChatOpen) ? 'none' : 'block',
        }}
      >
        {/* Single PIP video element - srcObject updated via useEffect */}
        <video
          ref={pipVideoRef}
          autoPlay
          muted={isHost}
          playsInline
          controls={false}
          disablePictureInPicture
          disableRemotePlayback
          controlsList="nodownload nofullscreen noremoteplayback"
          preload="auto"
          className="w-full h-full object-cover bg-black"
          style={{
            // ✅ PIP rotation and mirroring logic:
            // - Host sees their own video in PIP: mirror like a selfie (scaleX(-1))
            // - Guest sees host in PIP (remote video): currently upside down, so flip it 180°
            transform: isHost
              ? 'scaleX(-1)' // Host PIP: mirror like front camera selfie
              : `rotate(${pipRotation}deg)`, // Guest PIP: use calculated rotation (remove +180° since it's making it upside down)
            pointerEvents: 'none' // Ensure video element itself doesn't capture events
          }}
          // @ts-ignore - webkit specific attributes
          webkit-playsinline="true"
          x-webkit-airplay="deny"
          x5-playsinline="true"
          x5-video-player-type="h5"
          x5-video-player-fullscreen="false"
          x5-video-orientation="portrait"
        />

        {/* Only show connecting for guest when they're actually connecting */}
        {!isHost && connectionStatus === 'connecting' && !remoteConnected && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="text-center text-white">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto mb-1"></div>
              <p className="text-xs">Connecting...</p>
            </div>
          </div>
        )}

        {connectionStatus === 'failed' && (
          <div className="absolute inset-0 flex items-center justify-center bg-red-900/50">
            <div className="text-center text-white">
              <VideoIcon className="w-6 h-6 mx-auto mb-1" />
              <p className="text-xs">Failed</p>
            </div>
          </div>
        )}

        {/* Video Off Overlay - PIP */}
        {/* For Host: show when their own video is off */}
        {/* For Guest: show when host's video is off */}
        {((isHost && isVideoOff) || (!isHost && remoteConnected && !remoteVideoEnabled)) && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            <div className="text-center text-white">
              <VideoIcon className="w-6 h-6 mx-auto mb-1 text-gray-400" />
              <p className="text-xs">Camera off</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
